import { createApiCall } from "services/api/createApiCall";
import { transformErrorResponse } from "services/api/transformation";

import {
  transformPricingBreakdown,
  transformPricingCombinationOption,
  transformPricingHistoryRow,
  transformPricingKeyRow,
  transformPricingPack,
  transformPricingVersion,
  transformPricingVersionOnSave,
  transformPricingVersionRow,
} from "./transformations";
import {
  PricingBreakdown,
  PricingCombinationOption,
  PricingHistoryRow,
  PricingKeyRow,
  PricingPack,
  PricingVersion,
  PricingVersionEditFormValues,
  PricingVersionRow,
} from "./types";

const BASE = "/v1/admin/pricing";

/*---------------------------------------------------------------------------------------
    CJENIK — POPIS VERZIJA
-----------------------------------------------------------------------------------------*/
export const getPricingVersionsAPI = async (): Promise<PricingVersionRow[]> => {
  const response = await createApiCall(
    {
      path: `${BASE}/versions`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return (response.data?.results ?? []).map(transformPricingVersionRow);
};

/*---------------------------------------------------------------------------------------
    CJENIK — JEDNA VERZIJA
-----------------------------------------------------------------------------------------*/
export const getPricingVersionAPI = async (versionId: string): Promise<PricingVersion> => {
  const response = await createApiCall(
    {
      path: `${BASE}/versions/${versionId}`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformPricingVersion(response.data);
};

/*---------------------------------------------------------------------------------------
    CJENIK — VERZIJA KOJA VRIJEDI NA DATUM
-----------------------------------------------------------------------------------------*/
export const getEffectivePricingVersionAPI = async (
  onDate?: string
): Promise<PricingVersion> => {
  const query = onDate ? `?onDate=${onDate}` : "";

  const response = await createApiCall(
    {
      path: `${BASE}/versions/effective${query}`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformPricingVersion(response.data);
};

/*---------------------------------------------------------------------------------------
    CJENIK — SPREMANJE

    Nacrt se mijenja u mjestu; verzija koja je stupila na snagu traži noviji datum
    početka važenja i tada nastaje nova verzija.
-----------------------------------------------------------------------------------------*/
export const savePricingVersionAPI = async (
  formValues: PricingVersionEditFormValues
): Promise<PricingVersion> => {
  const response = await createApiCall(
    {
      path: `${BASE}/versions`,
      method: "POST",
      body: transformPricingVersionOnSave(formValues),
    },
    {
      getMessage: {
        success: () => "Cjenik je spremljen",
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformPricingVersion(response.data);
};

/*---------------------------------------------------------------------------------------
    CJENIK — BRISANJE NACRTA
-----------------------------------------------------------------------------------------*/
export const deletePricingDraftAPI = async (versionId: string): Promise<void> => {
  await createApiCall(
    {
      path: `${BASE}/versions/${versionId}`,
      method: "DELETE",
    },
    {
      getMessage: {
        success: () => "Nacrt cjenika je obrisan",
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();
};

/*---------------------------------------------------------------------------------------
    CJENIK — PACKOVI
-----------------------------------------------------------------------------------------*/
export const getPricingPacksAPI = async (): Promise<PricingPack[]> => {
  const response = await createApiCall(
    {
      path: `${BASE}/packs`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return (response.data?.results ?? []).map(transformPricingPack);
};

/*---------------------------------------------------------------------------------------
    CJENIK — ŠIFRARNIK KLJUČEVA
-----------------------------------------------------------------------------------------*/
export const getPricingKeysAPI = async (): Promise<PricingKeyRow[]> => {
  const response = await createApiCall(
    {
      path: `${BASE}/keys`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return (response.data?.results ?? []).map(transformPricingKeyRow);
};

/*---------------------------------------------------------------------------------------
    CJENIK — KOMBINACIJE PACKOVA

    Namjerno ne dohvaćamo cijelu verziju: ona nosi nabavne cijene i marže.
-----------------------------------------------------------------------------------------*/
export const getPricingCombinationsAPI = async (
  onDate?: string
): Promise<PricingCombinationOption[]> => {
  const query = onDate ? `?onDate=${onDate}` : "";

  const response = await createApiCall(
    {
      path: `${BASE}/combinations${query}`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return (response.data?.results ?? []).map(transformPricingCombinationOption);
};

/*---------------------------------------------------------------------------------------
    RAZRJEŠAVANJE

    Jedini put do cijene. `pricingSchemeId` je za čarobnjak — shema je ondje tek odabrana,
    a još nije dodijeljena klijentu.
-----------------------------------------------------------------------------------------*/
export const resolvePricingAPI = async (
  keyCode: string,
  tenantId: string,
  onDate?: string,
  pricingSchemeId?: string | null
): Promise<PricingBreakdown> => {
  const params = new URLSearchParams({ keyCode, tenantId });

  if (onDate) {
    params.set("onDate", onDate);
  }

  if (pricingSchemeId) {
    params.set("pricingSchemeId", pricingSchemeId);
  }

  const response = await createApiCall(
    {
      path: `${BASE}/resolve?${params.toString()}`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformPricingBreakdown(response.data);
};

/*---------------------------------------------------------------------------------------
    RAZRJEŠAVANJE VIŠE KLJUČEVA

    Čarobnjaku treba sedam-osam vrijednosti pri svakoj promjeni odabira; ovako je to jedan
    zahtjev umjesto rafala. Nabavne ključeve backend odbija.
-----------------------------------------------------------------------------------------*/
export const resolveManyPricingAPI = async (
  tenantId: string,
  keyCodes: string[],
  onDate?: string,
  pricingSchemeId?: string | null
): Promise<PricingBreakdown[]> => {
  const params = new URLSearchParams({ tenantId, keyCodes: keyCodes.join(",") });

  if (onDate) {
    params.set("onDate", onDate);
  }

  if (pricingSchemeId) {
    params.set("pricingSchemeId", pricingSchemeId);
  }

  const response = await createApiCall(
    {
      path: `${BASE}/resolve-many?${params.toString()}`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return (response.data?.results ?? []).map(transformPricingBreakdown);
};

/*---------------------------------------------------------------------------------------
    CJENIK — POVIJEST PROMJENA
-----------------------------------------------------------------------------------------*/
export const getPricingHistoryAPI = async (): Promise<PricingHistoryRow[]> => {
  const response = await createApiCall(
    {
      path: `${BASE}/history`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return (response.data?.results ?? []).map(transformPricingHistoryRow);
};
